'use client'

import React from 'react'

import { mockCopyBundle } from './fixtures'
import type { CopyBundle, CopyContextValue, SupportedLocales } from './types'

type CopyContextProps = {
  children: React.ReactNode
} & CopyContextValue

export const CopyContext = React.createContext<CopyContextValue>({
  appName: '',
  locale: 'en',
  copy: {},
})

/**
 * Hook which returns the last known value of a CopyBundle. It can be null
 * initially, but once a truthy value is set, any subsequent falsy values are
 * ignored. Subsequent truthy values will update the last known value.
 */
function useLastKnownCopyBundle(copy: CopyBundle | null): CopyBundle | null {
  const ref = React.useRef<CopyBundle | null>(copy)

  if (copy !== null) {
    ref.current = copy
  }

  return ref.current
}

export function CopyProvider({
  children,
  appName,
  locale,
  copy,
  onError,
}: CopyContextProps) {
  const copyBundle = useLastKnownCopyBundle(copy)

  return (
    <CopyContext.Provider
      value={{ appName, locale, copy: copyBundle, onError }}
    >
      {children}
    </CopyContext.Provider>
  )
}

export function MockCopyProvider({
  children = <h1>The app</h1>,
  locale = 'en',
  copy = mockCopyBundle,
}: Omit<CopyContextProps, 'appName'>) {
  return (
    <CopyProvider appName="app.mock-web-app" locale={locale} copy={copy}>
      {children}
    </CopyProvider>
  )
}

export function createMockCopyProvider(
  locale: SupportedLocales,
  copy: CopyContextProps['copy'],
) {
  return function Wrapper({ children }: { children: JSX.Element }) {
    return (
      <MockCopyProvider locale={locale} copy={copy}>
        {children}
      </MockCopyProvider>
    )
  }
}
